import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  aggregateBenchmarkTrials,
  MAX_MANNEQUIN_BENCHMARK_BUDGET,
  MAX_MANNEQUIN_PROPOSAL_BLOCK,
  runMannequinBenchmark,
  summarizeBenchmarkPairedWins,
  type BenchmarkEnvelopePoint,
  type BenchmarkErrorMetricName,
  type BenchmarkPairedOutcome,
  type BenchmarkPairedWinScope,
  type BenchmarkPairedWinSummary,
  type MannequinBenchmarkResult,
  type MetricEnvelope,
} from "../adaptive-sampling/mannequinBenchmark";
import type { CampaignConfig } from "../adaptive-sampling/types";
import { Icons } from "../thread-v4/icons";

type Props = {
  config: CampaignConfig;
  output: string;
  truthSeed: number;
  aircraftLabel: string;
  compact?: boolean;
  scaleStudy?: boolean;
  autoRun?: boolean;
  initialCheckpoint?: number;
  pointBudget?: number;
  maxPointBudget?: number;
  showPointControl?: boolean;
  onPointBudgetChange?: (budget: number) => void;
};

type ErrorMetricKey = "rmse" | "gradientRmse" | "sobolevH1";
type WeightedMetricKey = "weightedRmse" | "weightedGradientRmse" | "weightedSobolevH1";
type MetricKey = ErrorMetricKey | WeightedMetricKey | "coverage";
type MethodKey = "tailor" | "lhs";
type StallRegionKey = "preStall" | "transition" | "postStall";
type RegionMultipliers = Record<StallRegionKey, number>;

const CHART = { width: 720, height: 220, left: 54, right: 18, top: 20, bottom: 36 } as const;
const DEFAULT_SCALE_CHECKPOINTS = [56, 128, 500, 1_000, 5_000, 10_000] as const;
const LARGE_BENCHMARK_BUDGET = 1_000;
const UNIFORM_REGION_MULTIPLIERS: RegionMultipliers = { preStall: 1, transition: 1, postStall: 1 };
const STALL_AWARE_REGION_MULTIPLIERS: RegionMultipliers = { preStall: 1, transition: 3, postStall: 2 };
const STALL_REGIONS: ReadonlyArray<{
  key: StallRegionKey;
  label: string;
  description: string;
}> = [
  { key: "preStall", label: "Pre-stall", description: "before the local lift break" },
  { key: "transition", label: "Stall transition", description: "around the first sustained CL slope reversal" },
  { key: "postStall", label: "Post-stall", description: "beyond the local lift break" },
];
const WIN_METRICS: ReadonlyArray<{ key: BenchmarkErrorMetricName; label: string }> = [
  { key: "sobolevH1", label: "Sobolev H¹" },
  { key: "rmse", label: "RMSE" },
  { key: "gradientRmse", label: "Gradient" },
];

function compactNumber(value: number, digits = 3): string {
  if (!Number.isFinite(value)) return "–";
  if (Math.abs(value) > 0 && Math.abs(value) < 0.001) return value.toExponential(2);
  return value.toLocaleString(undefined, { maximumFractionDigits: digits });
}

const EMPTY_ENVELOPE: MetricEnvelope = { min: Number.NaN, median: Number.NaN, max: Number.NaN };

function seriesEnvelope(
  point: BenchmarkEnvelopePoint,
  method: MethodKey,
  metric: MetricKey,
  outputName?: string,
): MetricEnvelope {
  if (metric === "weightedRmse") return point[method].weighted.rmse;
  if (metric === "weightedGradientRmse") return point[method].weighted.gradientRmse;
  if (metric === "weightedSobolevH1") return point[method].weighted.sobolevH1;
  if (outputName && metric !== "coverage") {
    return point[method].outputs[outputName]?.[metric] ?? EMPTY_ENVELOPE;
  }
  return point[method][metric];
}

function metricValues(data: BenchmarkEnvelopePoint[], metric: MetricKey, outputName?: string): number[] {
  return data.flatMap((point) => [
    seriesEnvelope(point, "tailor", metric, outputName).min,
    seriesEnvelope(point, "tailor", metric, outputName).max,
    seriesEnvelope(point, "lhs", metric, outputName).min,
    seriesEnvelope(point, "lhs", metric, outputName).max,
  ]).filter(Number.isFinite);
}

function chartDomain(data: BenchmarkEnvelopePoint[], metric: MetricKey, outputName?: string): [number, number] {
  if (metric === "coverage") return [0, 1];
  const values = metricValues(data, metric, outputName);
  const max = Math.max(...values, 0.01);
  return [0, max * 1.08];
}

function hasMetric(data: BenchmarkEnvelopePoint[], metric: MetricKey, outputName?: string): boolean {
  return data.some((point) => Number.isFinite(seriesEnvelope(point, "tailor", metric, outputName).median)
    && Number.isFinite(seriesEnvelope(point, "lhs", metric, outputName).median));
}

function scale(value: number, min: number, max: number, start: number, end: number): number {
  if (!Number.isFinite(value) || max <= min) return start;
  return start + ((value - min) / (max - min)) * (end - start);
}

function budgetScale(value: number, min: number, max: number): number {
  return scale(Math.log(Math.max(1, value)), Math.log(Math.max(1, min)), Math.log(Math.max(1, max)), CHART.left, CHART.width - CHART.right);
}

function pathFor(
  data: BenchmarkEnvelopePoint[],
  method: MethodKey,
  metric: MetricKey,
  field: keyof MetricEnvelope,
  yDomain: [number, number],
  outputName?: string,
): string {
  const budgets = data.map((point) => point.budget);
  const minBudget = Math.min(...budgets);
  const maxBudget = Math.max(...budgets);
  return data.map((point, index) => {
    const x = budgetScale(point.budget, minBudget, maxBudget);
    const value = seriesEnvelope(point, method, metric, outputName)[field];
    const y = scale(value, yDomain[0], yDomain[1], CHART.height - CHART.bottom, CHART.top);
    return `${index ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
}

function bandFor(
  data: BenchmarkEnvelopePoint[],
  method: MethodKey,
  metric: MetricKey,
  yDomain: [number, number],
  outputName?: string,
): string {
  if (!data.length) return "";
  const upper = pathFor(data, method, metric, "max", yDomain, outputName);
  const lower = [...data].reverse().map((point) => {
    const budgets = data.map((item) => item.budget);
    const x = budgetScale(point.budget, Math.min(...budgets), Math.max(...budgets));
    const value = seriesEnvelope(point, method, metric, outputName).min;
    const y = scale(value, yDomain[0], yDomain[1], CHART.height - CHART.bottom, CHART.top);
    return `L${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");
  return `${upper} ${lower} Z`;
}

function EnvelopeChart({ data, metric, title, note, outputName }: {
  data: BenchmarkEnvelopePoint[];
  metric: MetricKey;
  title: string;
  note: string;
  outputName?: string;
}) {
  const yDomain = chartDomain(data, metric, outputName);
  const budgets = data.map((point) => point.budget);
  const yTicks = Array.from({ length: 5 }, (_, index) => yDomain[0] + ((yDomain[1] - yDomain[0]) * index) / 4);
  return (
    <article className="v5-benchmark-chart">
      <header><strong>{title}</strong><span>{note}</span></header>
      <svg viewBox={`0 0 ${CHART.width} ${CHART.height}`} role="img" aria-label={`${title}, Tailor versus LHS min median max`}>
        {yTicks.map((tick) => {
          const y = scale(tick, yDomain[0], yDomain[1], CHART.height - CHART.bottom, CHART.top);
          return (
            <g key={tick}>
              <line className="v5-benchmark-gridline" x1={CHART.left} x2={CHART.width - CHART.right} y1={y} y2={y} />
              <text className="v5-benchmark-axis-label" x={CHART.left - 8} y={y + 4} textAnchor="end">
                {metric === "coverage" ? `${Math.round(tick * 100)}%` : compactNumber(tick)}
              </text>
            </g>
          );
        })}
        {budgets.map((budget) => {
          const x = budgetScale(budget, Math.min(...budgets), Math.max(...budgets));
          return <text className="v5-benchmark-axis-label" key={budget} x={x} y={CHART.height - 12} textAnchor="middle">{budget.toLocaleString()}</text>;
        })}
        <path className="v5-benchmark-band is-lhs" d={bandFor(data, "lhs", metric, yDomain, outputName)} />
        <path className="v5-benchmark-band is-tailor" d={bandFor(data, "tailor", metric, yDomain, outputName)} />
        <path className="v5-benchmark-line is-lhs" d={pathFor(data, "lhs", metric, "median", yDomain, outputName)} />
        <path className="v5-benchmark-line is-tailor" d={pathFor(data, "tailor", metric, "median", yDomain, outputName)} />
        <text className="v5-benchmark-axis-title" x={(CHART.left + CHART.width - CHART.right) / 2} y={CHART.height - 1} textAnchor="middle">completed samples</text>
      </svg>
    </article>
  );
}

function percent(value: number): string {
  return Number.isFinite(value) ? `${Math.round(value * 100)}%` : "–";
}

function ratio(value: number): string {
  if (value === Number.POSITIVE_INFINITY) return "∞";
  return Number.isFinite(value) ? `${compactNumber(value, 2)}×` : "–";
}

function PairedOutcomeCell({ outcome }: { outcome?: BenchmarkPairedOutcome }) {
  if (!outcome || !outcome.comparisons) return <span className="v5-benchmark-win-empty">–</span>;
  return (
    <span className="v5-benchmark-win-cell" aria-label={`${outcome.wins} wins, ${outcome.ties} ties, ${outcome.losses} losses from ${outcome.comparisons} paired trials`}>
      <strong>{percent(outcome.winRate)}</strong>
      <small>{outcome.wins}–{outcome.ties}–{outcome.losses}</small>
    </span>
  );
}

function WinRateMatrix({
  label,
  scope,
  outputs,
}: {
  label: string;
  scope: BenchmarkPairedWinScope;
  outputs: MannequinBenchmarkResult["outputs"];
}) {
  const rows = [
    { name: "All-output", label: "All-output normalised", metrics: scope.aggregate },
    ...outputs.flatMap((output) => scope.outputs[output.name]
      ? [{ name: output.name, label: output.label, metrics: scope.outputs[output.name]! }]
      : []),
  ];
  return (
    <section className="v5-benchmark-win-matrix" aria-label={`${label} paired win rates by response and metric`}>
      <header><strong>{label}</strong><span>material Tailor win share · W–T–L</span></header>
      <div className="v5-benchmark-win-table" role="table">
        <div className="v5-benchmark-win-row is-head" role="row">
          <span role="columnheader">Response</span>
          {WIN_METRICS.map((metric) => <span role="columnheader" key={metric.key}>{metric.label}</span>)}
        </div>
        {rows.map((row, index) => (
          <div className={`v5-benchmark-win-row${index === 0 ? " is-aggregate" : ""}`} role="row" key={row.name}>
            <strong role="rowheader">{row.label}</strong>
            {WIN_METRICS.map((metric) => <PairedOutcomeCell key={metric.key} outcome={row.metrics[metric.key]} />)}
          </div>
        ))}
      </div>
    </section>
  );
}

function WinRateHeadline({
  label,
  outcome,
  primary = false,
}: {
  label: string;
  outcome: BenchmarkPairedOutcome;
  primary?: boolean;
}) {
  return (
    <article className={`v5-benchmark-win-headline${primary ? " is-primary" : ""}`}>
      <span>{label}</span>
      <div><strong>{percent(outcome.winRate)}</strong><em>{outcome.wins} W · {outcome.ties} T · {outcome.losses} L</em></div>
      <small>
        95% Wilson {percent(outcome.winRateWilson95[0])}–{percent(outcome.winRateWilson95[1])}
        {" · paired median LHS/Tailor "}{ratio(outcome.medianLhsTailorRatio)}
      </small>
    </article>
  );
}

function PairedWinResults({
  summary,
  outputs,
  weightingIsUniform,
  weightingLabel,
  initialDesignSize,
  varyTruthSeed,
}: {
  summary: BenchmarkPairedWinSummary;
  outputs: MannequinBenchmarkResult["outputs"];
  weightingIsUniform: boolean;
  weightingLabel: string;
  initialDesignSize: number;
  varyTruthSeed: boolean;
}) {
  const weightedSobolev = summary.weighted.aggregate.sobolevH1;
  const weightedRmse = summary.weighted.aggregate.rmse;
  const weightedGradient = summary.weighted.aggregate.gradientRmse;
  const budget = summary.budget ?? 0;
  const seedPoints = Math.min(initialDesignSize, budget);
  const adaptivePoints = Math.max(0, budget - seedPoints);
  const currentWeightingLabel = weightingIsUniform ? "Uniform envelope" : `Current envelope ${weightingLabel}`;
  return (
    <section className="v5-benchmark-win-results" aria-labelledby="v5-benchmark-win-title">
      <header>
        <div>
          <span className="v5-eyebrow">Top-level result</span>
          <strong id="v5-benchmark-win-title">At equal solve cost, how often does Tailor&apos;s generated ADB beat the LHS-fit baseline at {budget.toLocaleString()} points?</strong>
          <p>{summary.pairedTrials} matched trials on the same truth and probes. Both point sets use the same residual-GP fitter, oracle probes, and score; only Tailor owns adaptive acquisition and its recorded decision diagnostics. Primary score: all-output range-normalized Sobolev H¹ — value plus the full normalized gradient field. A win clears a symmetric {Math.round(summary.equivalenceFraction * 100)}% LHS/Tailor error-ratio band; closer results are ties.</p>
        </div>
        <span>Tailor · {seedPoints} seed + {adaptivePoints} adaptive</span>
      </header>
      {adaptivePoints === 0 ? <p className="v5-benchmark-win-warning">No adaptive acquisitions at this N: this compares initial designs, not Tailor&apos;s adaptive policy.</p> : null}
      <div className="v5-benchmark-win-headlines">
        <WinRateHeadline primary label={`${currentWeightingLabel} · Sobolev H¹ (primary)`} outcome={weightedSobolev} />
        <WinRateHeadline label={`${currentWeightingLabel} · RMSE (value)`} outcome={weightedRmse} />
        <WinRateHeadline label={`${currentWeightingLabel} · gradient RMSE`} outcome={weightedGradient} />
      </div>
      <div className="v5-benchmark-win-matrices">
        <WinRateMatrix label={`${currentWeightingLabel} weights`} scope={summary.weighted} outputs={outputs} />
        {!weightingIsUniform ? <WinRateMatrix label="Uniform envelope weights (reference)" scope={summary.uniform} outputs={outputs} /> : null}
      </div>
      <p className="v5-benchmark-win-method">
        These are end-to-end ADB quality wins: Tailor chose the adaptive runs; the shared evaluation harness fitted and scored both completed databases. W–T–L is counted separately for every row and metric at this one final N; outputs and error definitions are never pooled. Ties remain in the win-rate denominator. Coverage is not mixed into error wins. Changing envelope weights after seeing these results is exploratory; freeze them before making a presentation claim. {varyTruthSeed
          ? "Trials also change aircraft truth, so this is a paired result over this declared aircraft sequence."
          : "With fixed aircraft truth, this measures the declared LHS re-seed experiment, not a universal Tailor win probability."}
      </p>
    </section>
  );
}

function RegionalErrorCell({
  point,
  outputName,
  region,
  rowMaximum,
}: {
  point: BenchmarkEnvelopePoint;
  outputName: string;
  region: StallRegionKey;
  rowMaximum: number;
}) {
  const tailorRegion = point.tailor.regions[region];
  const lhsRegion = point.lhs.regions[region];
  const tailor = tailorRegion.outputs[outputName]?.rmse ?? EMPTY_ENVELOPE;
  const lhs = lhsRegion.outputs[outputName]?.rmse ?? EMPTY_ENVELOPE;
  const probeCount = Math.max(tailorRegion.effectiveProbeCount.median, lhsRegion.effectiveProbeCount.median);
  const populated = probeCount > 0 && Number.isFinite(tailor.median) && Number.isFinite(lhs.median);
  if (!populated) return <span className="v5-benchmark-region-empty">no probes</span>;

  const x = (value: number) => scale(value, 0, rowMaximum, 4, 96);
  const label = STALL_REGIONS.find((item) => item.key === region)?.label ?? region;
  return (
    <div className="v5-benchmark-region-cell">
      <svg viewBox="0 0 100 30" role="img" aria-label={`${outputName} ${label} RMSE: Tailor median ${compactNumber(tailor.median)}, LHS median ${compactNumber(lhs.median)}`}>
        <line className="v5-benchmark-region-track" x1="4" x2="96" y1="9" y2="9" />
        <line className="v5-benchmark-region-track" x1="4" x2="96" y1="21" y2="21" />
        <line className="v5-benchmark-region-interval" x1={x(tailor.min)} x2={x(tailor.max)} y1="9" y2="9" />
        <circle className="v5-benchmark-region-dot" cx={x(tailor.median)} cy="9" r="3" />
        <line className="v5-benchmark-region-interval is-lhs" x1={x(lhs.min)} x2={x(lhs.max)} y1="21" y2="21" />
        <circle className="v5-benchmark-region-dot is-lhs" cx={x(lhs.median)} cy="21" r="3" />
      </svg>
      <span><em>T</em>{compactNumber(tailor.median)} <em>L</em>{compactNumber(lhs.median)}</span>
    </div>
  );
}

function StallRegionMatrix({
  point,
  outputs,
  transitionHalfWidth,
}: {
  point: BenchmarkEnvelopePoint;
  outputs: MannequinBenchmarkResult["outputs"];
  transitionHalfWidth: number;
}) {
  const noStallCount = Math.max(
    point.tailor.stallClassification.noStallInEnvelopeCount.median,
    point.lhs.stallClassification.noStallInEnvelopeCount.median,
  );
  const unresolvedCount = Math.max(
    point.tailor.stallClassification.unresolvedCount.median,
    point.lhs.stallClassification.unresolvedCount.median,
  );
  return (
    <section className="v5-benchmark-region-section" aria-labelledby="v5-benchmark-region-title">
      <header>
        <div>
          <strong id="v5-benchmark-region-title">Where the error lives</strong>
          <p>Same held-out probes, split by their position relative to the local primary lift-stall boundary. Lower is better.</p>
        </div>
        <span>
          Final {point.budget.toLocaleString()} points · coefficient RMSE · row-local scale
        </span>
      </header>
      <div className="v5-benchmark-region-grid">
        <div className="v5-benchmark-region-head" aria-hidden="true">
          <span>Response</span>
          {STALL_REGIONS.map((region) => <span key={region.key}>{region.label}</span>)}
        </div>
        {outputs.map((output) => {
          const rowValues = STALL_REGIONS.flatMap((region) => {
            const tailor = point.tailor.regions[region.key].outputs[output.name]?.rmse;
            const lhs = point.lhs.regions[region.key].outputs[output.name]?.rmse;
            return [tailor?.max, lhs?.max].filter((value): value is number => value != null && Number.isFinite(value));
          });
          const rowMaximum = Math.max(...rowValues, 1e-12) * 1.05;
          return (
            <div className="v5-benchmark-region-row" key={output.name}>
              <strong>{output.label}<small>response units</small></strong>
              {STALL_REGIONS.map((region) => (
                <RegionalErrorCell
                  key={region.key}
                  point={point}
                  outputName={output.name}
                  region={region.key}
                  rowMaximum={rowMaximum}
                />
              ))}
            </div>
          );
        })}
      </div>
      <p>
        Regions are oracle-labelled for evaluation only; Tailor never sees these labels while choosing points. The transition spans ±{compactNumber(transitionHalfWidth, 1)}° around each held condition&apos;s first sustained CL slope reversal.
        {noStallCount > 0 || unresolvedCount > 0
          ? ` Outside the three regimes: ${compactNumber(noStallCount, 0)} no-stall-in-envelope and ${compactNumber(unresolvedCount, 0)} unresolved probes; both remain neutral at 1×.`
          : ""}
      </p>
    </section>
  );
}

const OUTPUT_DISPLAY_ORDER: Record<string, number> = {
  CL: 0,
  CD: 1,
  CY: 2,
  Cl: 3,
  Cm: 4,
  Cn: 5,
};

const PER_OUTPUT_METRICS: ReadonlyArray<{
  metric: ErrorMetricKey;
  label: string;
  note: string;
}> = [
  { metric: "rmse", label: "oracle RMSE", note: "coefficient error · lower is better" },
  { metric: "gradientRmse", label: "gradient RMSE", note: "all active input derivatives · lower is better" },
  { metric: "sobolevH1", label: "Sobolev H¹", note: "value + full gradient seminorm · lower is better" },
];

export function TailorMannequinBenchmark({
  config,
  output,
  truthSeed,
  aircraftLabel,
  compact = false,
  scaleStudy = false,
  autoRun = false,
  initialCheckpoint,
  pointBudget,
  maxPointBudget = MAX_MANNEQUIN_BENCHMARK_BUDGET,
  showPointControl = true,
  onPointBudgetChange,
}: Props) {
  const budgetCeiling = Math.max(
    8,
    Math.min(MAX_MANNEQUIN_BENCHMARK_BUDGET, Math.trunc(maxPointBudget)),
  );
  const defaultRepeats = scaleStudy ? (compact ? 2 : 3) : compact ? 6 : 10;
  const initialBudget = Math.max(
    8,
    Math.min(budgetCeiling, pointBudget ?? initialCheckpoint ?? (compact ? 56 : 64)),
  );
  const [repeats, setRepeats] = useState(
    initialBudget > LARGE_BENCHMARK_BUDGET || (scaleStudy && budgetCeiling > LARGE_BENCHMARK_BUDGET)
      ? 1
      : defaultRepeats,
  );
  const [budget, setBudget] = useState(Math.max(
    8,
    Math.min(budgetCeiling, initialCheckpoint ?? (compact ? 56 : 64)),
  ));
  const [lhsSeed, setLhsSeed] = useState(101);
  const [varyTruthSeed, setVaryTruthSeed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MannequinBenchmarkResult | null>(null);
  const [regionMultipliers, setRegionMultipliers] = useState<RegionMultipliers>({ ...UNIFORM_REGION_MULTIPLIERS });
  const runToken = useRef(0);
  const autoRunKey = useRef<string | null>(null);
  const repeatsEdited = useRef(false);
  const effectiveBudget = Math.max(8, Math.min(budgetCeiling, Math.trunc(pointBudget ?? budget)));
  const dimensions = useMemo(() => config.inputs.filter((axis) => axis.mode === "range").length, [config.inputs]);
  const scaleCheckpoints = useMemo(
    () => [...new Set([
      ...(initialCheckpoint == null ? [] : [initialCheckpoint]),
      ...DEFAULT_SCALE_CHECKPOINTS,
    ].map((checkpoint) => Math.max(8, Math.min(budgetCeiling, Math.trunc(checkpoint)))))]
      .sort((left, right) => left - right),
    [budgetCeiling, initialCheckpoint],
  );
  const selectedRunBudget = scaleStudy
    ? (scaleCheckpoints[scaleCheckpoints.length - 1] ?? effectiveBudget)
    : effectiveBudget;
  const configKey = useMemo(() => JSON.stringify({ inputs: config.inputs, outputs: config.outputs, sampler: config.sampler }), [config.inputs, config.outputs, config.sampler]);

  useEffect(() => {
    if (repeatsEdited.current) return;
    setRepeats(selectedRunBudget > LARGE_BENCHMARK_BUDGET ? 1 : defaultRepeats);
  }, [defaultRepeats, selectedRunBudget]);

  useEffect(() => {
    setResult(null);
    setProgress(0);
    setError(null);
    runToken.current += 1;
    setRunning(false);
  }, [configKey, effectiveBudget, output, truthSeed]);

  const run = useCallback(async () => {
    const token = ++runToken.current;
    setRunning(true);
    setProgress(0);
    setError(null);
    try {
      const next = await runMannequinBenchmark({
        config,
        output,
        truthSeed,
        samplerSeed: lhsSeed,
        repeats,
        budget: selectedRunBudget,
        budgets: scaleStudy ? scaleCheckpoints : undefined,
        trancheSize: MAX_MANNEQUIN_PROPOSAL_BLOCK,
        varyTruthSeed,
        probeCount: scaleStudy ? 128 : undefined,
        onProgress: (complete, total) => {
          if (runToken.current === token) setProgress(complete / total);
        },
      });
      if (runToken.current === token) setResult(next);
    } catch (reason) {
      if (runToken.current === token) setError(reason instanceof Error ? reason.message : String(reason));
    } finally {
      if (runToken.current === token) setRunning(false);
    }
  }, [config, lhsSeed, output, repeats, scaleCheckpoints, scaleStudy, selectedRunBudget, truthSeed, varyTruthSeed]);

  useEffect(() => {
    if (!autoRun) return;
    const key = `${truthSeed}|${output}|${repeats}|${effectiveBudget}|${scaleStudy}|${scaleCheckpoints.join("-")}`;
    if (autoRunKey.current === key) return;
    autoRunKey.current = key;
    const timer = window.setTimeout(() => void run(), pointBudget == null ? 0 : 400);
    return () => window.clearTimeout(timer);
  }, [autoRun, effectiveBudget, output, pointBudget, repeats, run, scaleCheckpoints, scaleStudy, truthSeed]);

  const displayedEnvelope = useMemo(
    () => result ? aggregateBenchmarkTrials(result.trials, regionMultipliers) : [],
    [regionMultipliers, result],
  );
  const last = displayedEnvelope[displayedEnvelope.length - 1];
  const coverageDelta = last ? last.tailor.coverage.median - last.lhs.coverage.median : null;
  const weightingIsUniform = STALL_REGIONS.every(({ key }) => regionMultipliers[key] === 1);
  const weightingLabel = STALL_REGIONS.map(({ key }) => compactNumber(regionMultipliers[key], 2)).join(":");
  const displayedOutputs = result
    ? [...result.outputs].sort((left, right) => (OUTPUT_DISPLAY_ORDER[left.name] ?? 99) - (OUTPUT_DISPLAY_ORDER[right.name] ?? 99))
    : [];
  const pairedWins = useMemo(
    () => result ? summarizeBenchmarkPairedWins(result.trials, regionMultipliers) : null,
    [regionMultipliers, result],
  );

  return (
    <section className={`v5-mannequin-benchmark${compact ? " is-compact" : ""}`} data-testid="mannequin-repeat-benchmark">
      <header className="v5-benchmark-head">
        <div>
          <span className="v5-eyebrow">End-to-end ADB generation experiment</span>
          <strong>Closed-loop Tailor generator vs full-budget re-seeded LHS baseline</strong>
          <p>{aircraftLabel} · {dimensions}-D · all {config.outputs.length} configured outputs · {scaleStudy ? "Python Tailor checkpoint study" : "same truth, probes, and solve budget within each trial"}</p>
        </div>
        <div className="v5-benchmark-controls">
          <label><span>Trials N</span><input aria-label="Benchmark trial count" type="number" min={1} max={scaleStudy ? 10 : 30} value={repeats} onChange={(event) => {
            repeatsEdited.current = true;
            setRepeats(Math.max(1, Math.min(scaleStudy ? 10 : 30, Number(event.target.value))));
          }} /></label>
          {scaleStudy
            ? <span className="v5-benchmark-checkpoints"><em>Checkpoints</em><strong>{scaleCheckpoints.map((checkpoint) => checkpoint.toLocaleString()).join(" · ")}</strong></span>
            : showPointControl ? (
              <label className="v5-benchmark-point-slider">
                <span>N points <strong>{effectiveBudget.toLocaleString()}</strong></span>
                <input
                  aria-label="Benchmark point count"
                  type="range"
                  min={8}
                  max={budgetCeiling}
                  step={4}
                  value={effectiveBudget}
                  onChange={(event) => {
                    const nextBudget = Math.max(8, Math.min(budgetCeiling, Number(event.target.value)));
                    setBudget(nextBudget);
                    onPointBudgetChange?.(nextBudget);
                  }}
                />
              </label>
            ) : null}
          {selectedRunBudget > LARGE_BENCHMARK_BUDGET
            ? <span className="v5-benchmark-checkpoints"><em>Execution</em><strong>exact N={selectedRunBudget.toLocaleString()} · ≤128 points/request</strong></span>
            : null}
          <label><span>LHS design seed</span><input aria-label="LHS design random seed" type="number" min={0} max={1_000_000} value={lhsSeed} onChange={(event) => setLhsSeed(Math.max(0, Math.trunc(Number(event.target.value))))} /></label>
          <button className="v5-text-button" type="button" onClick={() => setLhsSeed((seed) => (seed + 7919) % 1_000_001)} disabled={running}>
            <Icons.Refresh size={12} />Reseed LHS
          </button>
          <label className="v5-adb-switch">
            <input aria-label="New aircraft each trial" type="checkbox" checked={varyTruthSeed} onChange={(event) => setVaryTruthSeed(event.target.checked)} />
            <span /><em>New aircraft each trial</em>
          </label>
          <button className="v5-primary-button" type="button" onClick={run} disabled={running}>
            <Icons.Sparkle size={13} />{running && selectedRunBudget > LARGE_BENCHMARK_BUDGET
              ? `Streaming N=${selectedRunBudget.toLocaleString()}…`
              : running
                ? `Running ${Math.round(progress * repeats)}/${repeats}`
                : `Run ${repeats} ${scaleStudy ? "checkpoint " : ""}trials`}
          </button>
        </div>
      </header>

      <section className="v5-benchmark-role-contrast" aria-labelledby="v5-benchmark-role-title">
        <header>
          <span className="v5-eyebrow">What is actually being compared</span>
          <strong id="v5-benchmark-role-title">This is an ADB generator benchmark—not two equivalent sampling products.</strong>
          <p>The common surrogate, truth probes, plots, and error measures make the completed databases comparable. They do not turn LHS into an adaptive generator.</p>
        </header>
        <div>
          <article className="is-tailor">
            <span>Tailor · ADB generator</span>
            <strong>Observe → fit → diagnose → choose → repeat</strong>
            <p>Refits six output-specific ARD residual GPs, then combines uncertainty, gradients, topology, downstream relevance, coverage, and any declared priority to choose the next solve.</p>
          </article>
          <article className="is-lhs">
            <span>LHS · control baseline</span>
            <strong>Generate N coordinates once</strong>
            <p>Selects all N coordinates before any response is seen. LHS itself has no surrogate, posterior, acquisition rule, or point rationale; the GP surfaces and error diagnostics shown here come from the shared post-sampling evaluation harness.</p>
          </article>
        </div>
      </section>

      <section className="v5-benchmark-measure" aria-label="Envelope error scoring">
        <header>
          <div className="v5-benchmark-measure-copy">
            <span className="v5-eyebrow">Evaluation measure</span>
            <strong>Weight error by where it appears in the envelope</strong>
            <p>Scoring only — these weights change neither database nor acquisition, and they apply equally to Tailor and LHS.</p>
          </div>
          <div className="v5-benchmark-measure-actions">
            <button
              className="v5-text-button"
              type="button"
              disabled={weightingIsUniform}
              onClick={() => setRegionMultipliers({ ...UNIFORM_REGION_MULTIPLIERS })}
            >
              Reset 1:1:1
            </button>
            <button
              className="v5-text-button"
              type="button"
              aria-pressed={regionMultipliers.preStall === 1 && regionMultipliers.transition === 3 && regionMultipliers.postStall === 2}
              onClick={() => setRegionMultipliers({ ...STALL_AWARE_REGION_MULTIPLIERS })}
            >
              Stall-aware 1:3:2
            </button>
          </div>
        </header>
        <div className="v5-benchmark-region-controls">
          {STALL_REGIONS.map((region) => (
            <label className="v5-benchmark-region-control" key={region.key}>
              <span><strong>{region.label}</strong><em>{compactNumber(regionMultipliers[region.key], 2)}×</em></span>
              <input
                aria-label={`${region.label} error weight`}
                type="range"
                min={0}
                max={3}
                step={0.25}
                value={regionMultipliers[region.key]}
                onChange={(event) => {
                  const value = Number(event.target.value);
                  setRegionMultipliers((current) => {
                    const next = { ...current, [region.key]: value };
                    return Object.values(next).some((weight) => weight > 0) ? next : current;
                  });
                }}
              />
              <small>{region.description}</small>
            </label>
          ))}
        </div>
      </section>

      {running ? <div className="v5-benchmark-progress"><i style={{ width: `${progress * 100}%` }} /><span>{Math.round(progress * 100)}%</span></div> : null}
      {error ? <div className="v5-adb-builder-error"><Icons.Info size={13} />{error}</div> : null}
      {result ? (
        <>
          {pairedWins ? (
            <PairedWinResults
              summary={pairedWins}
              outputs={displayedOutputs}
              weightingIsUniform={weightingIsUniform}
              weightingLabel={weightingLabel}
              initialDesignSize={result.protocol.initialDesignSize}
              varyTruthSeed={varyTruthSeed}
            />
          ) : null}
          <div className="v5-benchmark-summary" aria-label="Repeated trial summary">
            <span><em>Aircraft truths</em><strong>{varyTruthSeed ? `${truthSeed}…${truthSeed + result.repeats - 1}` : truthSeed}</strong></span>
            <span><em>Tailor seed design</em><strong>{result.protocol.initialDesignSize} points</strong><small>seed {result.protocol.initialDesignSeed}…</small></span>
            <span><em>Tailor adaptive phase</em><strong>{Math.max(0, (last?.budget ?? 0) - result.protocol.initialDesignSize)} solves</strong><small>acquisition seed {result.protocol.tailorAcquisitionSeed}</small></span>
            <span><em>Full-N LHS designs</em><strong>{lhsSeed}…{lhsSeed + result.repeats - 1}</strong><small>non-adaptive · rebuilt at each checkpoint</small></span>
            <span><em>Coverage Δ</em><strong>{coverageDelta == null ? "–" : `${coverageDelta >= 0 ? "+" : ""}${compactNumber(coverageDelta * 100, 1)} pt`}</strong><small>Tailor − LHS</small></span>
          </div>
          <div className="v5-benchmark-legend" aria-label="Benchmark legend">
            <span className="is-tailor"><i />Tailor-generated ADB median <em>band = min–max</em></span>
            <span className="is-lhs"><i />LHS design + shared-fit median <em>band = min–max</em></span>
            <span><em>Point selection differs; posterior fitting and evaluation are shared</em></span>
            <span><em>Global multi-output acquisition remains objective-neutral · envelope weights affect scoring only</em></span>
            <span><em>Each LHS checkpoint is an independent full N-stratum design, not a union of smaller tranches</em></span>
            <span><em>Aggregate errors divide each coefficient by its range on the shared truth probes before weighted RMS combination; fixed physical ranges are the legacy fallback</em></span>
            <span><em>{result.surrogate.label} · {result.surrogate.kind === "streaming-active-set-gp"
              ? "all N solves retained; posterior conditioning bounded at 128 observations"
              : "exact dense residual GP through N=128"}</em></span>
          </div>
          {last ? (
            <StallRegionMatrix
              point={last}
              outputs={displayedOutputs}
              transitionHalfWidth={result.protocol.regionalEvaluation.transitionHalfWidth}
            />
          ) : null}
          <div className="v5-benchmark-grid">
            {!weightingIsUniform ? (
              <>
                <EnvelopeChart
                  data={displayedEnvelope}
                  metric="weightedRmse"
                  title="Envelope-weighted all-output RMSE"
                  note={`pre:transition:post ${weightingLabel} · scoring only`}
                />
                <EnvelopeChart
                  data={displayedEnvelope}
                  metric="weightedGradientRmse"
                  title="Envelope-weighted gradient RMSE"
                  note={`all active input derivatives · ${weightingLabel}`}
                />
                <EnvelopeChart
                  data={displayedEnvelope}
                  metric="weightedSobolevH1"
                  title="Envelope-weighted Sobolev H¹"
                  note={`value + full gradient seminorm · ${weightingLabel}`}
                />
              </>
            ) : null}
            <EnvelopeChart data={displayedEnvelope} metric="rmse" title="Uniform all-output normalised RMSE" note="unweighted diagnostic · lower is better" />
            <EnvelopeChart data={displayedEnvelope} metric="coverage" title={`${result.dimensions}-D domain coverage`} note="higher is better" />
            {hasMetric(displayedEnvelope, "gradientRmse")
              ? <EnvelopeChart data={displayedEnvelope} metric="gradientRmse" title="All-output normalised-gradient RMSE" note="output range + input-axis normalised · lower is better" />
              : null}
            {hasMetric(displayedEnvelope, "sobolevH1")
              ? <EnvelopeChart data={displayedEnvelope} metric="sobolevH1" title="All-output normalised Sobolev H¹" note="range-normalised value + full gradient L² seminorm" />
              : null}
            {displayedOutputs.flatMap((benchmarkOutput) => PER_OUTPUT_METRICS.flatMap((definition) =>
              hasMetric(displayedEnvelope, definition.metric, benchmarkOutput.name)
                ? [<EnvelopeChart
                    key={`${benchmarkOutput.name}-${definition.metric}`}
                    data={displayedEnvelope}
                    metric={definition.metric}
                    outputName={benchmarkOutput.name}
                    title={`${benchmarkOutput.label} ${definition.label}`}
                    note={`${definition.note} · weight ${compactNumber(benchmarkOutput.weight, 2)}`}
                  />]
                : [],
            ))}
          </div>
        </>
      ) : (
        <div className="v5-benchmark-empty">
          <Icons.GridIcon size={18} />
          <span>Run one trial for a re-seedable comparison, or N trials to expose the best/worst-case envelope.</span>
        </div>
      )}
    </section>
  );
}
